import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa6";
import { GAME_STAGES, DEFAULT_ROOM } from "../constants/gameStages";
import { SocketContext } from "../context/SocketProvider";
import { useGameStage } from "../hooks/useGameStage";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { useTimer } from "../context/TimerContext";
import { useLoading } from "../context/LoadingContext";
import { useLanguage } from "../context/LanguageContext";
import { useFullscreen } from "../hooks/useFullscreen";
import { usePersistentFullscreen } from "../hooks/usePersistentFullscreen";
import ImageEnlargeOverlay from "./ImageEnlargeOverlay";

/**
 * DescribeImagePage component allows the describer to write a description for the selected image.
 * It handles the image selection, description input, and submission to the server.
 */
const DescribeImagePage = () => {
  // Socket context to access socket and update game stage
  const { socket } = useContext(SocketContext);
  const { updateStage } = useGameStage();
  const { t, language } = useLanguage();
  const { requestFullscreen } = useFullscreen();
  const { startPersistentMode, stopPersistentMode, forceFullscreen } = usePersistentFullscreen();

  // Modal state for popping out images
  const [enlargedImage, setEnlargedImage] = useState(null); // For image overlay
  const [selectedImage, setSelectedImage] = useState(null); // {filename: "_.jpg", path: "./images/filename", url: "http:// + backendurl + path", index: 0-7}
  const [description, setDescription] = useState(""); 
  const { loading, setLoading } = useLoading(); 
  const navigate = useNavigate();
  const { isExpired } = useTimer();

  // Challenge words state and handler
  const [challengeWords, setChallengeWords] = useState([]);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState("");
  // Track which challenge words are used in the description
  const getChallengeWordStatus = (word) => {
    // For Japanese text, we can't use \b word boundaries, so we check for exact substring match
    // For English, we still want word boundaries to avoid partial matches
    const isJapanese = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(word);
    
    if (isJapanese) {
      // For Japanese words, do a simple case-insensitive substring search
      return description.toLowerCase().includes(word.toLowerCase());
    } else {
      // For English words, use word boundaries
      const regex = new RegExp(`\\b${word}\\b`, "i");
      return regex.test(description);
    }
  };
  const { bgStyle, className } = useRoleBackground("pink");

  const handleGenerateWords = async () => {
    setChallengeLoading(true);
    setChallengeError("");
    try {
      if (language === 'ja') {
        // Fetch 4 random Japanese words from JLPT vocab API
        const requests = Array.from({ length: 4 }, () =>
        fetch("https://jlpt-vocab-api.vercel.app/api/words/random").then(res => res.json())
        );
        const results = await Promise.all(requests);
        const words = results.map(item => item.word);
        setChallengeWords(words);
      } else {
        // Fetch 4 random English words from random-word-api.vercel.app
        const res = await fetch("https://random-word-api.vercel.app/api?words=4");
        if (!res.ok) throw new Error("Failed to fetch English words");
        const words = await res.json();
        setChallengeWords(words);
      }
    } catch (err) {
      setChallengeError("Could not fetch words. Try again.");
    } finally {
      setChallengeLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setEnlargedImage(image);
  };

  const closeEnlargedImage = () => {
    setEnlargedImage(null);
  };

  useEffect(() => {
    if (challengeWords.length === 0) {
      handleGenerateWords();
    }
  }, []);

  // Regenerate words when language changes
  useEffect(() => {
    if (challengeWords.length > 0) {
      handleGenerateWords();
    }
  }, [language]);

  // Load selected image from state
  const { state } = useLocation();
  useEffect(() => {
    if (!state?.selectedImage) {
      navigate("/pickingimage");
      return;
    }
    setSelectedImage(state.selectedImage);
    setLoading(false);
  }, [state, navigate, setLoading]);
  const allImages = state?.allImages || [];

  // Aggressive fullscreen maintenance for mobile keyboard
  useEffect(() => {
    let fullscreenInterval;
    let isInputFocused = false;
    
    // Continuously monitor and maintain fullscreen when input is focused
    const maintainFullscreen = () => {
      if (isInputFocused && 
          !document.fullscreenElement && 
          !document.webkitFullscreenElement && 
          !document.mozFullScreenElement && 
          !document.msFullscreenElement) {
        requestFullscreen();
      }
    };

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
        isInputFocused = true;
        // Start aggressive monitoring
        fullscreenInterval = setInterval(maintainFullscreen, 100);
        // Also try immediate re-entry after delays
        setTimeout(maintainFullscreen, 200);
        setTimeout(maintainFullscreen, 500);
        setTimeout(maintainFullscreen, 1000);
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
        isInputFocused = false;
        if (fullscreenInterval) {
          clearInterval(fullscreenInterval);
          fullscreenInterval = null;
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(maintainFullscreen, 100);
      }
    };

    // Prevent default fullscreen exit on certain events
    const preventFullscreenExit = (e) => {
      if (isInputFocused) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Try to prevent fullscreen exit
    document.addEventListener('fullscreenchange', preventFullscreenExit, true);
    document.addEventListener('webkitfullscreenchange', preventFullscreenExit, true);

    return () => {
      isInputFocused = false;
      if (fullscreenInterval) {
        clearInterval(fullscreenInterval);
      }
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', preventFullscreenExit, true);
      document.removeEventListener('webkitfullscreenchange', preventFullscreenExit, true);
    };
  }, [requestFullscreen]);

  const handleSubmit = () => {
    if (description.trim() || isExpired) {
      if (!selectedImage || typeof selectedImage.index !== "number") {
        console.error("Selected image is invalid or missing index.");
        return;
      }

      // Calculate which challenge words were used
      const usedChallengeWords = challengeWords.filter(word => getChallengeWordStatus(word));

      // Emit the description and image paths to the server
      socket.emit("submit_description", {
        room: DEFAULT_ROOM,
        images: allImages,
        description: description.trim() || t('error.noDescription', 'No description provided.'),
        image_describer: selectedImage.path,
        challengeWords: challengeWords, // All challenge words
        usedChallengeWords: usedChallengeWords, // Only the words that were used
      });
      socket.emit("pause_timer", { room: DEFAULT_ROOM });

      updateStage(GAME_STAGES.WAITING_FOR_GUESSER);
      navigate("/waitingforguesser");
    }
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setDescription(newDescription); // Update state
  };

  // Handle Enter key in textarea to submit
  const handleTextareaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault(); // Prevent new line
      // Only submit if button is not disabled
      if ((description.trim() || isExpired) && selectedImage) {
        handleSubmit();
      }
    }
    // Shift+Enter creates new line (default behavior)
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!selectedImage) {
    return (
      <div
        className={`min-h-screen flex flex-col items-center justify-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl mb-4">No image selected</div>
        <button
          className="bg-aicom_pink text-white px-6 py-3 rounded font-bold"
          onClick={() => navigate("/pickingimage")}
        >
          Go Back to Select Image
        </button>
      </div>
    );
  }

  return (
    <div className={`p-[64px] ${className}`} style={bgStyle}>
      <div className="flex flex-col items-center text-center">
        <div className="flex gap-[24px] rounded-[15px] items-stretch">
          {/* Description and special words column */}
          <div className="flex-1 min-h-[628px] w-full flex flex-col">
            <div className="rounded-[15px] shadow-lg h-[445px] bg-white relative">
              {/* Description input area */}
              <h2 className="bg-pink400 rounded-t-[15px] font-bold text-white text-[22px] px-[23px] py-[11px]">
                {t('describer.writeDescription', 'WRITE A DESCRIPTION THAT THE GUESSER WILL UNDERSTAND BUT AICOM-CHAN WON’T!')}
              </h2>
              <div className="px-[23px] py-[24px] justify-end flex flex-col relative">
                <textarea
                  disabled={isExpired}
                  value={description}
                  onChange={handleDescriptionChange}
                  onKeyDown={handleTextareaKeyDown}
                  onFocus={() => {
                    // Start aggressive fullscreen maintenance
                    forceFullscreen();
                    startPersistentMode();
                  }}
                  onBlur={() => {
                    // Stop aggressive maintenance when not focused
                    stopPersistentMode();
                  }}
                  placeholder={
                    isExpired ? t('timer.timesUp', "Time's up!") : t('describer.placeholderWithEnter', "Write your description here...\nPress Enter to submit")
                  }
                  className="w-full rounded-[15px] bg-white pb-[29px] resize-none focus:border-aicom_pink focus:outline-none text-[26px]"
                  maxLength={500}
                />
              </div>
              <button
                disabled={(!description.trim() && !isExpired) || !selectedImage}
                onClick={handleSubmit}
                className={`btn-pink-sm btn-bottom-right-sm`}
              >
                <span className={isExpired ? "" : "btn-text-pos-right-sm"}>
                  {isExpired ? t('timer.timesUp', "Time's up") : t('button.done', "Done!")}
                </span>
                {!isExpired && (
                  <FaChevronRight className="arrow-pos-right-sm" />
                )}
              </button>
            </div>
            <div className="flex-1 mt-[24px] rounded-[15px] shadow-lg bg-white flex flex-col">
              <h2 className="bg-pink400 rounded-t-[15px] font-bold text-white text-[22px] px-[12px] py-[10px]">
                {t('describer.challengeWords', 'CHALLENGE WORDS:')}
              </h2>
              <ul className="grid grid-cols-2 gap-y-[15px] text-[20px] text-black pl-[24px] py-[24px] overflow-y-auto flex-1">
                {challengeWords.length > 0 ? (
                  challengeWords.map((word, idx) => {
                    const used = getChallengeWordStatus(word);
                    return (
                      <li key={idx} className="flex items-center gap-2">
                        {/* Icon: pink star if used, light pink star if not */}
                        <span>
                          {used ? (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="#D71991"
                              stroke="#D71991"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="inline-block align-middle"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="#FFD2F3"
                              stroke="#FFD2F3"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="inline-block align-middle"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          )}
                        </span>
                        <span>{word}</span>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-gray-400">
                    Press the button to generate words
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Display the selected image */}
          <div className="h-full w-full bg-pink400 rounded-2xl shadow-lg flex flex-1 flex-col items-center p-[24px] justify-start ">
            <div className="flex justify-center rounded-2xl shadow ">
              <img
                src={selectedImage.url}
                alt="Selected image"
                className="object-cover rounded"
              />
            </div>
            <p className="mt-[24px] text-white font-bold flex self-start">
              {t('describer.imagesToAvoid', 'IMAGES TO AVOID:')}
            </p>
            <div className=" grid grid-cols-7 gap-[8px] items-start self-start h-fit">
              {(() => {
                const allImages = state?.allImages || [];

                // Filter out the selected image
                return allImages
                  .map((img, idx) => ({ ...img, idx }))
                  .filter((img) => img.idx !== selectedImage.index)
                  .map((img, idx) => (
                    <div
                      key={idx}
                      className="flex cursor-pointer"
                      onClick={() => handleImageClick(img)}
                    >
                      <img
                        src={img.url}
                        alt={`Other image ${idx + 1}`}
                        className="mt-[8px] w-[60px] h-[60px] object-cover rounded shadow hover:ring-4 hover:ring-aicom_pink transition-all"
                      />
                    </div>
                  ));
              })()}
            </div>
          </div>
        </div>

        {/* Image Enlargement Overlay */}
        <ImageEnlargeOverlay 
          image={enlargedImage} 
          onClose={closeEnlargedImage} 
        />
        
        {/* Action buttons */}
        <div className="flex flex-col items-center gap-4 mt-8">
          <button
            className="btn-pink-sm-transparent !w-[240px]"
            onClick={() =>
              navigate("/pickingimage", {
                state: { shouldRefetch: false, allImages: state?.allImages }, // Pass current images if available
              })
            }
          >
            {t("button.backToImageSelection", "Back to Image Selection")}
          </button>
        </div>
      </div>
    </div>
  );
};
export default DescribeImagePage;
